import { useState, useEffect, useRef } from "react";
import { getMultiYearSummary, formatCurrency } from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";

const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];
const FREE_YEARS = [2023, 2024, 2025];

export default function MultiYearChart({ npi, year, setYear, isPaidTier, providerName }) {  
  const { setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set(["ALL"]));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [chartView, setChartView] = useState("total"); // "total" | "stacked" | "normalized"
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const availableYears = isPaidTier ? ALL_YEARS : FREE_YEARS;  

  useEffect(() => {
    setLoading(true);
    getMultiYearSummary(npi)
      .then((d) => setData(isPaidTier ? d : d.filter((row) => row.year >= 2023)))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [npi, isPaidTier]);  

  // Get unique companies sorted by total payments
  const companies = [...new Set(data.map((d) => d.companyName))]
    .sort((a, b) => a.localeCompare(b));

  function toggleCompany(name) {
    if (name === "ALL") {
      setSelectedCompanies(new Set(["ALL"]));
      return;
    }
    const next = new Set(selectedCompanies);
    next.delete("ALL");
    if (next.has(name)) {
      next.delete(name);
      if (next.size === 0) next.add("ALL");
    } else {
      next.add(name);
    }
    setSelectedCompanies(next);
  }

  const isAll = selectedCompanies.has("ALL");

  // Aggregate by year based on selected companies
  const yearTotals = availableYears.map((year) => {    
    const rows = data.filter(
      (d) => d.year === year && (isAll || selectedCompanies.has(d.companyName))
    );
    return {
      year,
      total: rows.reduce((s, r) => s + r.totalPayments, 0),
      count: rows.reduce((s, r) => s + r.paymentCount, 0),
    };
  });

  const maxTotal = Math.max(...yearTotals.map((y) => y.total), 1);
  const grandTotal = yearTotals.reduce((s, y) => s + y.total, 0);

  // Companies sorted by total payments desc for stacked chart
  const companiesByTotal = [...new Set(data.map((d) => d.companyName))]
    .sort((a, b) => {
      const aVal = data.filter((d) => d.companyName === a).reduce((s, r) => s + r.totalPayments, 0);
      const bVal = data.filter((d) => d.companyName === b).reduce((s, r) => s + r.totalPayments, 0);
      return bVal - aVal;
    });

  const COMPANY_COLORS = [
    "rgba(234,179,8,0.8)",
    "rgba(139,92,246,0.8)",
    "rgba(34,197,94,0.8)",
    "rgba(59,130,246,0.8)",
    "rgba(239,68,68,0.8)",
    "rgba(249,115,22,0.8)",
    "rgba(20,184,166,0.8)",
    "rgba(236,72,153,0.8)",
  ];

  const companyColorMap = new Map(
    companiesByTotal.map((c, i) => [c, COMPANY_COLORS[i % COMPANY_COLORS.length]])
  );

  const visibleCompanies = isAll
    ? companiesByTotal
    : companiesByTotal.filter((c) => selectedCompanies.has(c));

  // Stacked year data — one segment per visible company per year
  const stackedYearData = availableYears.map((y) => {
    const segments = visibleCompanies.map((company) => {
      const row = data.find((d) => d.year === y && d.companyName === company);
      return { company, value: row ? row.totalPayments : 0 };
    });
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    return { year: y, segments, total };
  });

  const maxStackedTotal = Math.max(...stackedYearData.map((y) => y.total), 1);

  function slicerLabel() {
    if (isAll) return "All companies";
    if (selectedCompanies.size === 1) {
      const name = [...selectedCompanies][0];
      return name.length > 35 ? name.slice(0, 35) + "..." : name;
    }
    return `${selectedCompanies.size} companies selected`;
  }

  if (loading) {
    return (
      <div className="card mb-6 animate-pulse">
        <div className="h-4 bg-white/10 rounded w-1/3 mb-4" />
        <div className="flex items-end gap-2 h-32">
          {(isPaidTier ? ALL_YEARS : FREE_YEARS).map((y) => (            
            <div key={y} className="flex-1 bg-white/5 rounded-t" style={{ height: "40%" }} />
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) return null;

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Payment history</h2>

        <div className="print:hidden flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center bg-navy-700 border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setChartView("total")}
              className={`text-xs px-2.5 py-1.5 transition-all ${
                chartView === "total"
                  ? "bg-gold-500/20 text-gold-400"
                  : "text-white/40 hover:text-white/60"
              }`}
            >
              Total
            </button>
            <button
              onClick={() => setChartView("stacked")}
              className={`text-xs px-2.5 py-1.5 transition-all ${
                chartView === "stacked"
                  ? "bg-gold-500/20 text-gold-400"
                  : "text-white/40 hover:text-white/60"
              }`}
            >
              $
            </button>
            <button
              onClick={() => setChartView("normalized")}
              className={`text-xs px-2.5 py-1.5 transition-all ${
                chartView === "normalized"
                  ? "bg-gold-500/20 text-gold-400"
                  : "text-white/40 hover:text-white/60"
              }`}
            >
              %
            </button>
          </div>

          {/* Multi-select slicer */}
          <div className="relative" ref={dropdownRef}>          
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 bg-navy-700 border border-white/10 text-white/70 rounded-lg px-3 py-1.5 text-xs hover:border-white/20 transition-colors"
          >
            <span>{slicerLabel()}</span>
            <span className="text-white/30">{dropdownOpen ? "▲" : "▼"}</span>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1 bg-navy-800 border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl min-w-56 max-h-64 overflow-y-auto">
              {/* All companies option */}
              <button
                onClick={() => toggleCompany("ALL")}
                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 transition-colors border-b border-white/5 ${
                  isAll ? "text-gold-400 bg-gold-500/10" : "text-white/60 hover:text-white hover:bg-navy-700"
                }`}
              >
                <span className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${isAll ? "bg-gold-500 border-gold-500" : "border-white/20"}`}>
                  {isAll && <span className="text-navy-900 text-xs leading-none">✓</span>}
                </span>
                All companies
              </button>

              {/* Individual companies */}
              {companies.map((name) => {
                const selected = selectedCompanies.has(name);
                return (
                  <button
                    key={name}
                    onClick={() => toggleCompany(name)}
                    className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 transition-colors border-b border-white/5 last:border-0 ${
                      selected ? "text-gold-400 bg-gold-500/5" : "text-white/60 hover:text-white hover:bg-navy-700"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-gold-500 border-gold-500" : "border-white/20"}`}>
                      {selected && <span className="text-navy-900 text-xs leading-none">✓</span>}
                    </span>
                    <span className="truncate">{name.length > 35 ? name.slice(0, 35) + "..." : name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="relative mb-2" style={{ height: "220px" }}>
        <div className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-8">

          {/* ── Total view ─────────────────────────────────────────────── */}
          {chartView === "total" && yearTotals.map(({ year: barYear, total, count }) => {
            const heightPct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
            const hasData = total > 0;
            return (
              <div
                key={barYear}
                className="flex-1 flex flex-col items-center justify-end h-full group relative"
                onClick={() => { if (hasData && setYear) setYear(barYear); }}
                style={{ cursor: hasData ? "pointer" : "default" }}
              >
                {hasData && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                    <p className="text-gold-400 font-medium">{formatCurrency(total)}</p>
                    <p className="text-white/40">{count} payment{count !== 1 ? "s" : ""}</p>
                  </div>
                )}
                {hasData && (
                  <div className="absolute w-full text-center pointer-events-none"
                    style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "2px" }}>
                    <span className="text-gold-400/80" style={{ fontSize: "9px" }}>
                      {total >= 1000000 ? `$${(total / 1000000).toFixed(1)}M`
                        : total >= 1000 ? `$${(total / 1000).toFixed(0)}K`
                        : `$${total.toFixed(0)}`}
                    </span>
                  </div>
                )}
                <div
                  className={`w-full rounded-t transition-all ${
                    barYear === year ? "bg-gold-400"
                      : hasData ? "bg-gold-500/70 group-hover:bg-gold-500"
                      : "bg-white/5"
                  }`}
                  style={{ height: hasData ? `${Math.max(heightPct, 3)}%` : "3px" }}
                />
              </div>
            );
          })}

          {/* ── Stacked $ view ─────────────────────────────────────────── */}
          {chartView === "stacked" && stackedYearData.map(({ year: barYear, segments, total }) => {
            const heightPct = (total / maxStackedTotal) * 100;
            const activeSegments = segments.filter((s) => s.value > 0);
            const hasData = total > 0;
            return (
              <div
                key={barYear}
                className="flex-1 flex flex-col items-center justify-end h-full group relative"
                onClick={() => { if (hasData && setYear) setYear(barYear); }}
                style={{ cursor: hasData ? "pointer" : "default" }}
              >
                {/* Tooltip */}
                {hasData && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none"
                    style={{ minWidth: "140px" }}>
                    <div className="flex items-center justify-between gap-4 mb-1.5 pb-1.5 border-b border-white/10">
                      <p className="text-white/50 font-medium">{barYear}</p>
                      <p className="text-gold-400 font-medium">{formatCurrency(total)}</p>
                    </div>
                    {activeSegments.map(({ company, value }) => (
                      <div key={company} className="flex items-center justify-between gap-3 mt-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-sm shrink-0"
                            style={{ backgroundColor: companyColorMap.get(company) }} />
                          <span className="text-white/40 truncate" style={{ maxWidth: "90px" }}>
                            {company.length > 18 ? company.slice(0, 18) + "…" : company}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-white/60">{formatCurrency(value)}</span>
                          <span className="text-white/30">{total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0%"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Total label above bar */}
                {hasData && (
                  <div className="absolute w-full text-center pointer-events-none"
                    style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "2px" }}>
                    <span className="text-gold-400/80" style={{ fontSize: "9px" }}>
                      {total >= 1000000 ? `$${(total / 1000000).toFixed(1)}M`
                        : total >= 1000 ? `$${(total / 1000).toFixed(0)}K`
                        : `$${total.toFixed(0)}`}
                    </span>
                  </div>
                )}
                {/* Stacked bar */}
                <div
                  className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                  style={{ height: hasData ? `${Math.max(heightPct, 3)}%` : "3px" }}
                >
                  {segments.map(({ company, value }) =>
                    value > 0 ? (
                      <div key={company}
                        style={{
                          height: `${(value / total) * 100}%`,
                          backgroundColor: companyColorMap.get(company),
                          minHeight: "2px",
                        }}
                      />
                    ) : null
                  )}
                </div>
              </div>
            );
          })}

          {/* ── Normalized % view ──────────────────────────────────────── */}
          {chartView === "normalized" && stackedYearData.map(({ year: barYear, segments, total }) => {
            const hasData = total > 0;
            const normalized = segments.map((s) => ({
              ...s,
              pct: total > 0 ? (s.value / total) * 100 : 0,
            }));
            const activeSegments = normalized.filter((s) => s.value > 0);
            return (
              <div
                key={barYear}
                className="flex-1 flex flex-col items-center justify-end h-full group relative"
                onClick={() => { if (hasData && setYear) setYear(barYear); }}
                style={{ cursor: hasData ? "pointer" : "default" }}
              >
                {/* Tooltip */}
                {hasData && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none"
                    style={{ minWidth: "140px" }}>
                    <div className="flex items-center justify-between gap-4 mb-1.5 pb-1.5 border-b border-white/10">
                      <p className="text-white/50 font-medium">{barYear}</p>
                      <p className="text-gold-400 font-medium">100%</p>
                    </div>
                    {activeSegments.map(({ company, pct }) => (
                      <div key={company} className="flex items-center justify-between gap-3 mt-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-sm shrink-0"
                            style={{ backgroundColor: companyColorMap.get(company) }} />
                          <span className="text-white/40 truncate" style={{ maxWidth: "90px" }}>
                            {company.length > 18 ? company.slice(0, 18) + "…" : company}
                          </span>
                        </div>
                        <span className="text-white/60 shrink-0">{pct.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Normalized stacked bar */}
                <div
                  className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                  style={{ height: hasData ? "calc(100% - 24px)" : "3px" }}
                >
                  {normalized.map(({ company, value, pct }) =>
                    value > 0 ? (
                      <div key={company}
                        style={{
                          height: `${pct}%`,
                          backgroundColor: companyColorMap.get(company),
                          minHeight: "2px",
                        }}
                      />
                    ) : null
                  )}
                </div>
              </div>
            );
          })}

        </div>

        {/* Year labels */}
        <div className="absolute bottom-0 left-0 right-0 flex gap-1.5">
          {availableYears.map((barYear) => (
            <div key={barYear} className="flex-1 text-center">
              <span
                className={barYear === year ? "text-gold-400 font-medium" : "text-white/30"}
                style={{ fontSize: "10px" }}
              >
                {barYear}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Company legend for stacked views */}
      {(chartView === "stacked" || chartView === "normalized") && visibleCompanies.length > 1 && (
        <div className="print:hidden flex flex-wrap gap-x-4 gap-y-1 mb-2">
          {visibleCompanies.slice(0, 8).map((c) => (
            <div key={c} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm shrink-0"
                style={{ backgroundColor: companyColorMap.get(c), opacity: 0.8 }} />
              <span className="text-white/40 text-xs truncate max-w-32">
                {c.length > 25 ? c.slice(0, 25) + "..." : c}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Upgrade note for free users */}
      {!isPaidTier && (
        <p className="print:hidden text-white/30 text-xs mt-1">
          Showing 2023–present ·{" "}
          <button
            onClick={() => {
              setUpgradeMessage("Upgrade to Pro for full payment history back to 2016.");
              setShowUpgradeModal(true);
            }}
            className="text-gold-400 hover:text-gold-300 transition-colors"
          >
            Upgrade for full history back to 2016
          </button>          
        </p>
      )}

      {/* Export + grand total row — screen only */}
      <div className="print:hidden flex items-center justify-between mt-1">
        <button
          onClick={() => {
            if (!isPaidTier) {
              setUpgradeMessage("Exporting chart data is a Pro feature. Upgrade to download payment history as a CSV.");
              setShowUpgradeModal(true);
              return;
            }
            exportPaymentHistoryCsv(stackedYearData, yearTotals, chartView, providerName);
          }}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
        >
          {isPaidTier ? "⬇ Export to CSV" : "🔒 Export to CSV"}
        </button>
        <p className="text-white/30 text-xs text-right">     
        {formatCurrency(grandTotal)} total
        {!isAll && selectedCompanies.size === 1
          ? ` from ${[...selectedCompanies][0].length > 30 ? [...selectedCompanies][0].slice(0, 30) + "..." : [...selectedCompanies][0]}`
          : !isAll
          ? ` from ${selectedCompanies.size} selected companies`
          : " across all companies"}
      </p>
      </div>
    </div>
  );
}

function exportPaymentHistoryCsv(stackedYearData, yearTotals, chartView, providerName) {
  let rows;
  if (chartView === "total") {
    rows = [["Year", "Total Payments ($)", "Payment Count"]];
    for (const { year, total, count } of yearTotals) {
      if (total > 0) rows.push([year, total.toFixed(2), count]);
    }
  } else {
    // Collect all companies
    const companies = [...new Set(
      stackedYearData.flatMap((y) => y.segments.map((s) => s.company))
    )];
    rows = [["Year", "Company", "Payments ($)", "% of Year Total"]];
    for (const { year, segments, total } of stackedYearData) {
      if (total === 0) continue;
      for (const { company, value } of segments) {
        if (value > 0) {
          rows.push([
            year,
            company,
            value.toFixed(2),
            `${((value / total) * 100).toFixed(1)}%`,
          ]);
        }
      }
    }
  }
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${(providerName || "provider").replace(/\s+/g, "_")}_payment_history.csv`;
  a.click();
}